import { useCallback, useEffect, useRef, useState } from 'react';
import { GetToken } from '@utils/storage';
import * as config from '@config';

// Mirrors backend response.EligibilityView from
// internal/services/mozioredirect/response/response.go.
interface EligibilityResponse {
    mozioEnabled: boolean;
    mozioProvisioned: boolean;
    mozioCommissionRate: number;
    mozioCurrency: string;
    mozioLanguage: string;
}

// Discriminated state the page consumes. The hook collapses the flat
// backend flags into a single `kind` so callers don't have to re-derive it
// at every render.
export type EligibilityState =
    | { kind: 'loading' }
    | { kind: 'eligible'; commissionRate: number; currency: string; language: string }
    | { kind: 'agent_not_provisioned' }
    | { kind: 'agency_not_enabled' }
    | { kind: 'not_configured' }
    | { kind: 'error'; message: string };

interface UseTransferEligibility {
    state: EligibilityState;
    refetch: () => void;
}

function deriveState(body: EligibilityResponse): EligibilityState {
    if (!body.mozioEnabled) return { kind: 'agency_not_enabled' };
    if (!body.mozioProvisioned) return { kind: 'agent_not_provisioned' };
    return {
        kind: 'eligible',
        commissionRate: body.mozioCommissionRate,
        currency: body.mozioCurrency,
        language: body.mozioLanguage,
    };
}

function useTransferEligibility(): UseTransferEligibility {
    const [state, setState] = useState<EligibilityState>({ kind: 'loading' });
    const abortRef = useRef<AbortController | null>(null);
    const [tick, setTick] = useState(0);

    const refetch = useCallback(() => setTick((t) => t + 1), []);

    useEffect(() => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;
        setState({ kind: 'loading' });

        (async () => {
            try {
                const response = await fetch(`${(config as { apiURL2: string }).apiURL2}/transfer-redirects/eligibility`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${GetToken()}` },
                    signal,
                });
                const body = await response.json().catch(() => ({} as Record<string, unknown>));
                if (response.status === 200) {
                    setState(deriveState(body as EligibilityResponse));
                    return;
                }
                if (response.status === 503 && (body as { code?: string }).code === 'MOZIO_NOT_CONFIGURED') {
                    setState({ kind: 'not_configured' });
                    return;
                }
                const msg = (body as { error?: string; message?: string }).error
                    ?? (body as { message?: string }).message
                    ?? `请求失败 (${response.status})`;
                setState({ kind: 'error', message: msg });
            } catch (e) {
                if (signal.aborted) return;
                const msg = e instanceof Error ? e.message : String(e);
                setState({ kind: 'error', message: msg });
            }
        })();

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [tick]);

    return { state, refetch };
}

export default useTransferEligibility;
